/**
 * How an uploaded file is presented in a list row - the extension badge, the
 * label it is listed under, and the upload time.
 *
 * Shared by the order Uploads tab and the company diary's Files tab so the two
 * cannot drift into different badge colours for the same file type.
 */

/** One upload in flight, as every file list tracks it. */
export interface PendingFile {
    id: string | number;
    /** Kept so a failed upload can be retried without re-picking the file. */
    file: File;
    name: string;
    status: 'preparing' | 'uploading' | 'error';
    loaded: number;
    total: number;
    percent: number;
}

interface DisplayableFile {
    name?: string | null;
    file_name?: string | null;
    collection_name?: string | null;
    custom_properties?: Record<string, any> | null;
}

/** The file's extension, lowercased, or null when it has none. */
export function getFileExtension(file: DisplayableFile): string | null {
    const fileName = file.file_name || file.name || '';
    const lastDot = fileName.lastIndexOf('.');

    if (lastDot === -1 || lastDot === fileName.length - 1) {
        return null;
    }

    return fileName.slice(lastDot + 1).toLowerCase();
}

/** Background class for the extension badge sitting on the document icon. */
export function getExtensionBgClass(extension: string | null): string {
    switch ((extension || '').toLowerCase()) {
        case 'pdf':
            return 'bg-muval-red-1';
        case 'jpg':
        case 'jpeg':
            return 'bg-muval-blue-1';
        case 'png':
            return 'bg-muval-purple-1';
        default:
            return 'bg-muval-gray-3';
    }
}

/**
 * The label to list the file under: the operator's own label first, then the
 * uploader's description, then the stored name.
 */
export function getFileDisplayName(file: DisplayableFile): string {
    return file.custom_properties?.display_label || file.custom_properties?.info || file.file_name || file.name || '';
}

/** Human-readable byte count for an in-flight upload. */
export function formatBytes(bytes: number): string {
    if (!bytes) {
        return '0 B';
    }

    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`;
}
